<template>
  <div class="generation-panel mx-4 mb-2 pa-3">
    <div class="d-flex align-center flex-wrap">
      <v-icon small class="mr-2">mdi-robot-outline</v-icon>
      <span class="font-weight-medium mr-3">{{ $t('Generated text') }}</span>
      <v-chip small :color="presentation.color" dark class="mr-2">
        <v-icon x-small left>{{ presentation.icon }}</v-icon>
        {{ $t(presentation.label) }}
      </v-chip>
      <v-chip v-if="sourceLabel" small outlined class="mr-2">
        <v-icon x-small left>{{ staffUpload ? 'mdi-account-outline' : 'mdi-robot-outline' }}</v-icon>
        {{ $t(sourceLabel) }}
      </v-chip>
      <v-chip v-if="textType" small outlined class="mr-2" :title="$t('The type, language and gender the attached text was written as')">
        {{ $t('Text written as {0}', [textTypeLabel]) }}
      </v-chip>
      <v-spacer></v-spacer>
      <v-select
        :value="selectedType"
        :items="typeItems"
        :disabled="disabled || anyAction || running || typeLocked || switchingType"
        :loading="switchingType"
        :title="typeLocked ? $t('The cover design sets the type of this book') : $t('Book type')"
        dense
        outlined
        hide-details
        class="type-select mr-2"
        @change="switchType"
      ></v-select>
      <v-btn
        v-if="canGenerate"
        small
        color="primary"
        :disabled="disabled || generating"
        :loading="generating"
        @click="generate"
      >
        <v-icon small left>mdi-creation</v-icon>
        {{ generateLabel }}
      </v-btn>
      <v-btn
        v-else-if="status === 'unsupported'"
        small
        text
        :disabled="disabled || generating"
        :loading="generating"
        :title="$t('After changing the design, check whether this book can be generated now')"
        @click="generate"
      >
        <v-icon small left>mdi-refresh</v-icon>
        {{ $t('Check again') }}
      </v-btn>
      <v-btn v-else-if="running" small disabled>
        <v-progress-circular indeterminate size="14" width="2" class="mr-2" />
        {{ $t('Generating…') }}
      </v-btn>
      <v-btn
        v-if="running"
        small
        outlined
        color="error"
        class="ml-2"
        :disabled="disabled || anyAction"
        @click="confirmCancel = true"
      >
        <v-icon small left>mdi-cancel</v-icon>
        {{ $t('Cancel generation') }}
      </v-btn>
      <v-btn
        v-if="canRegenerate"
        small
        outlined
        class="ml-2"
        :disabled="disabled || anyAction"
        @click="confirmRegenerate = true"
      >
        <v-icon small left>mdi-book-refresh-outline</v-icon>
        {{ $t('Regenerate book') }}
      </v-btn>
    </div>

    <v-alert v-if="confirmRegenerate && canRegenerate" type="warning" dense outlined class="mt-3 mb-0">
      <div class="mb-2">
        {{
          $t(
            'Generate the whole book again as {0} in {1}? The current text is replaced once the new one is ready. This costs one generation (about €0.40).',
            [bookTypeLabel, bookLanguageName]
          )
        }}
      </div>
      <v-btn small color="warning" :loading="regeneratingBook" :disabled="disabled" @click="regenerateBook">
        {{ $t('Yes, regenerate') }}
      </v-btn>
      <v-btn small text :disabled="regeneratingBook" @click="confirmRegenerate = false">{{ $t('Cancel') }}</v-btn>
    </v-alert>

    <v-alert v-if="confirmCancel && running" type="error" dense outlined class="mt-3 mb-0">
      <div class="mb-2">
        {{ $t('Stop generating this text? gravur stops the job now; a text attached so far stays. Generate starts a new job later.') }}
      </div>
      <v-btn small color="error" :loading="cancelling" :disabled="disabled" @click="cancelGeneration">
        {{ $t('Yes, cancel generation') }}
      </v-btn>
      <v-btn small text :disabled="cancelling" @click="confirmCancel = false">{{ $t('Keep running') }}</v-btn>
    </v-alert>

    <div v-if="meta.length" class="caption text--secondary mt-1">
      {{ meta.join(' · ') }}
    </div>

    <v-progress-linear v-if="running || continuing" indeterminate height="2" class="mt-2"></v-progress-linear>
    <div v-if="running" class="caption text--secondary mt-1">
      {{ $t('gravur is writing the text. This takes a few minutes; this view refreshes by itself.') }}
    </div>
    <div v-else-if="continuing" class="caption text--secondary mt-1">
      {{ $t('Text attached. The book is being assembled in the background…') }}
    </div>

    <v-alert v-if="textTypeMismatch" type="warning" dense text class="mt-3 mb-0">
      {{ $t('The attached text was written as {0}, the book is a {1} book. Generate it again as {1}.', [textTypeName, bookTypeLabel]) }}
    </v-alert>

    <v-alert v-if="textLanguageMismatch" type="warning" dense text class="mt-3 mb-0">
      {{ $t('The attached text is in {0}, the book in {1}. It can only be assembled with a text in {1}.', [textLanguageName, bookLanguageName]) }}
    </v-alert>

    <v-alert v-if="genderMismatch" type="warning" dense text class="mt-3 mb-0">
      {{
        $t('The attached text was written for {0} as {1}, the book now says {2}. It can only be assembled with a text written as {2}.', [
          genderSubjectName,
          $t(genderName(genderMismatch.written)),
          $t(genderName(genderMismatch.book)),
        ])
      }}
    </v-alert>

    <v-alert v-if="status === 'unsupported'" type="info" dense text class="mt-3 mb-0">
      {{ $t('This book type is not generated automatically yet. Upload the text PDF below as before.') }}
      <div v-if="generation.error" class="caption mt-1">{{ generation.error }}</div>
    </v-alert>

    <v-alert v-if="status === 'failed'" type="error" dense text class="mt-3 mb-0">
      <div class="font-weight-medium">{{ $t('Text generation failed') }}</div>
      <div class="caption">{{ generation.error || book.lastError || $t('No reason given') }}</div>
    </v-alert>

    <v-alert v-else-if="status === 'cancelled'" type="info" dense text class="mt-3 mb-0">
      {{ $t('Generation was cancelled. Generate starts a new job.') }}
    </v-alert>

    <v-alert v-else-if="status === 'pending' && generation.error" type="warning" dense text class="mt-3 mb-0">
      {{ $t('gravur did not accept the request yet; it is retried automatically.') }}
      <div class="caption">{{ generation.error }}</div>
    </v-alert>

    <v-alert
      v-if="book.lastError && status !== 'failed' && status !== 'unsupported'"
      type="warning"
      dense
      text
      class="mt-3 mb-0"
    >
      {{ $t('Last automated step failed') }}: {{ book.lastError }}
    </v-alert>

    <v-alert v-if="staffUpload && status !== 'none'" type="info" dense text class="mt-3 mb-0">
      {{ $t('Staff uploaded the text PDF; that upload is used, not the generated text.') }}
    </v-alert>

    <!-- Review: warnings by section, each section can be rewritten; then release as it is. -->
    <div v-if="groups.length && (status === 'needs_review' || status === 'completed')" class="mt-3">
      <div class="caption text--secondary mb-1">
        {{
          status === 'needs_review'
            ? $t('gravur flagged these sections. Regenerate them or approve the text as it is.')
            : $t('Remarks gravur left on the text')
        }}
      </div>
      <v-simple-table dense class="warnings-table">
        <tbody>
          <tr v-for="group in groups" :key="group.sectionId || 'report'">
            <td class="section-cell">
              <div class="font-weight-medium">{{ group.label || $t('Whole report') }}</div>
              <div v-if="group.sectionId && group.label !== group.sectionId" class="caption text--secondary">
                {{ group.sectionId }}
              </div>
            </td>
            <td>
              <v-chip
                v-for="item in group.kinds"
                :key="item.kind"
                x-small
                outlined
                color="warning"
                class="mr-1 my-1"
                :title="item.kind"
              >
                {{ $t(warningLabel(item.kind)) }}<span v-if="item.count > 1">&nbsp;× {{ item.count }}</span>
              </v-chip>
            </td>
            <td class="text-right action-cell">
              <v-btn
                v-if="group.sectionId"
                x-small
                outlined
                :disabled="disabled || anyAction"
                :loading="!!regenerating[group.sectionId]"
                @click="regenerate(group.sectionId)"
              >
                <v-icon x-small left>mdi-refresh</v-icon>
                {{ $t('Regenerate section') }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <!-- Page fit: chapters closing on a nearly empty page, tightened or rewritten to length. -->
    <div v-if="fit || canFit" class="mt-3">
      <div class="d-flex align-center">
        <span class="caption text--secondary mr-2">{{ $t('Page fit') }}</span>
        <v-chip v-if="fit" x-small :color="fitPresentation.color" dark class="mr-2">{{ $t(fitPresentation.label) }}</v-chip>
        <span v-if="fit" class="caption text--secondary">{{ fitSummary }}</span>
        <v-spacer></v-spacer>
        <v-btn v-if="canFit" x-small outlined :disabled="disabled || anyAction" :loading="fitting" @click="fitPages">
          <v-icon x-small left>mdi-page-layout-body</v-icon>
          {{ $t('Fit pages again') }}
        </v-btn>
      </div>
      <div v-if="fit && fit.squeezed && fit.squeezed.length" class="caption text--secondary mt-1">
        {{ $t('Tightened (type only)') }}: {{ fit.squeezed.join(', ') }}
      </div>
      <v-simple-table v-if="fit && fit.sections && fit.sections.length" dense class="warnings-table mt-1">
        <tbody>
          <tr v-for="section in fit.sections" :key="section.sectionId">
            <td class="section-cell">
              <div class="font-weight-medium">{{ section.heading || section.sectionId }}</div>
              <div class="caption text--secondary">{{ section.sectionId }}</div>
            </td>
            <td class="caption">
              {{ $t(section.action === 'shorten' ? 'Shortened' : 'Lengthened') }}:
              {{ section.words }} → {{ section.minWords }}–{{ section.maxWords }} {{ $t('words') }}
            </td>
            <td class="caption text-right">{{ $t('last page') }} {{ Math.round(section.lastPageFill * 100) }}%</td>
          </tr>
        </tbody>
      </v-simple-table>
      <div v-if="fit && fit.error" class="caption error--text mt-1">{{ fit.error }}</div>
    </div>

    <template v-if="status === 'needs_review'">
      <div v-if="!confirmApprove" class="mt-3">
        <v-btn small color="success" :disabled="disabled || anyAction" @click="confirmApprove = true">
          <v-icon small left>mdi-check-decagram-outline</v-icon>
          {{ $t('Approve') }}
        </v-btn>
      </div>
      <v-alert v-else type="success" dense outlined class="mt-3 mb-0">
        <div class="mb-2">
          {{ $t('Release this text into the book as it is? The book is then assembled automatically.') }}
        </div>
        <v-btn small color="success" :loading="approving" :disabled="disabled" @click="approve">
          {{ $t('Yes, approve') }}
        </v-btn>
        <v-btn small text :disabled="approving" @click="confirmApprove = false">{{ $t('Cancel') }}</v-btn>
      </v-alert>
    </template>
  </div>
</template>

<script>
  import moment from 'moment'
  import {
    approveHoroscopeContent,
    cancelHoroscopeGeneration,
    fitHoroscopePages,
    generateHoroscopeContent,
    regenerateHoroscopeSection,
    updateHoroscopeBook,
  } from '../services/pdfGenerationService'
  import {
    BOOK_TYPE_NAMES,
    CLASSIC_DESIGN_TYPES,
    GENERATED_TYPES,
    bookTypeName,
    genderName,
    generationStatus,
    groupWarnings,
    hasStaffUpload,
    isContinuing,
    isGenerating,
    isOtherGender,
    isOtherLanguage,
    isOtherType,
    languageName,
    statusPresentation,
    textBookType,
    textGenderMismatch,
    textGenders,
    textLanguage,
    warningLabel,
  } from './horoscopeGeneration'

  /**
   * One book's generated text (gravur): status, review warnings and the staff
   * actions. Every action answers with the book view, handed up as `view`;
   * messages go up as `notify` so the dialog's snackbar shows them.
   */
  export default {
    name: 'HoroscopeGenerationPanel',
    props: {
      book: { type: Object, required: true },
      orderId: { type: [String, Number], required: true },
      storeId: { type: [String, Number], required: true },
      disabled: { type: Boolean, default: false },
    },
    data() {
      return {
        generating: false,
        approving: false,
        confirmApprove: false,
        regenerating: {},
        // The type select's value: the book's type, or the one being saved.
        selectedType: this.book.bookType || null,
        switchingType: false,
        confirmRegenerate: false,
        regeneratingBook: false,
        fitting: false,
        confirmCancel: false,
        cancelling: false,
      }
    },
    computed: {
      generation() {
        return this.book.generation || {}
      },
      status() {
        return generationStatus(this.book)
      },
      presentation() {
        return statusPresentation(this.book)
      },
      running() {
        return isGenerating(this.book)
      },
      continuing() {
        return isContinuing(this.book)
      },
      staffUpload() {
        return hasStaffUpload(this.book)
      },
      sourceLabel() {
        if (!this.book.content) return null
        return this.staffUpload ? 'Source: staff upload' : 'Source: gravur'
      },
      /**
       * Request (or retry): a staff upload makes generated text pointless — it would be kept aside.
       * After a language (type, gender) switch the job on file is the old one, whatever its status.
       */
      canGenerate() {
        return (
          !this.staffUpload &&
          (this.status === 'none' ||
            this.status === 'failed' ||
            this.status === 'cancelled' ||
            this.otherLanguage ||
            this.otherType ||
            this.otherGender)
        )
      },
      otherGender() {
        return isOtherGender(this.book)
      },
      otherType() {
        return isOtherType(this.book)
      },
      /**
       * The whole book again (a new gravur job): a text exists or the job is
       * done, nothing runs and no staff upload — also after CPI (the dialog
       * warns; a new print order is an explicit resubmit). A switched type, language or
       * gender is a plain Generate instead.
       */
      canRegenerate() {
        const hasText = !!(this.book.content && this.book.content.source === 'gravur')
        const done = ['completed', 'needs_review', 'failed'].includes(this.status)
        return (
          (hasText || (done && this.status !== 'failed')) &&
          !this.running &&
          !this.staffUpload &&
          !this.canGenerate &&
          this.status !== 'unsupported'
        )
      },
      typeItems() {
        return Object.keys(BOOK_TYPE_NAMES).map((type) => {
          const generated = GENERATED_TYPES.includes(type)
          const name = this.$t(bookTypeName(type))
          return { value: type, text: generated ? name : `${name} (${this.$t('coming later')})`, disabled: !generated }
        })
      },
      /** A classic design is bound to its type; rest-api keeps it whatever is chosen. */
      typeLocked() {
        return !!CLASSIC_DESIGN_TYPES[String(this.book.variant || '').toUpperCase()]
      },
      bookTypeLabel() {
        return this.$t(bookTypeName(this.book.bookType))
      },
      textType() {
        return textBookType(this.book)
      },
      textTypeName() {
        return this.$t(bookTypeName(this.textType))
      },
      /** "Personality · DE · Female" — partners books "Female & Male"; parts not recorded are left out. */
      textTypeLabel() {
        const language = textLanguage(this.book)
        const genders = textGenders(this.book)
        const parts = [this.textTypeName]
        if (language) parts.push(language.toUpperCase())
        if (genders) {
          parts.push(
            [genders.native, genders.partner]
              .filter(Boolean)
              .map((gender) => this.$t(genderName(gender)))
              .join(' & ')
          )
        }
        return parts.join(' · ')
      },
      genderMismatch() {
        return textGenderMismatch(this.book)
      },
      /** Who the mismatch is about: the subject's name, else "the partner" / "the subject". */
      genderSubjectName() {
        const p = this.book.properties || {}
        const partner = this.genderMismatch && this.genderMismatch.subject === 'partner'
        return (partner ? p.namePartner : p.name) || this.$t(partner ? 'the partner' : 'the subject')
      },
      textTypeMismatch() {
        return !!(this.textType && this.book.bookType && this.textType !== this.book.bookType)
      },
      otherLanguage() {
        return isOtherLanguage(this.book)
      },
      generateLabel() {
        if (this.otherType) return this.$t('Generate as {0}', [this.bookTypeLabel])
        if (this.otherLanguage) return this.$t('Generate in {0}', [this.$t(languageName(this.book.language))])
        if (this.otherGender) return this.$t('Generate with the new gender')
        return this.status === 'failed' || this.status === 'cancelled' ? this.$t('Generate again') : this.$t('Generate text')
      },
      textLanguageMismatch() {
        const language = textLanguage(this.book)
        return !!(language && this.book.language && language !== this.book.language)
      },
      textLanguageName() {
        return this.$t(languageName(textLanguage(this.book)))
      },
      bookLanguageName() {
        return this.$t(languageName(this.book.language))
      },
      fit() {
        return this.generation.fit || null
      },
      fitPresentation() {
        return (
          {
            none: { label: 'Nothing to fit', color: 'success' },
            requested: { label: 'Rewriting chapters', color: 'info' },
            done: { label: 'Fitted', color: 'success' },
            failed: { label: 'Fit failed', color: 'error' },
          }[this.fit && this.fit.status] || { label: 'Unknown', color: 'grey' }
        )
      },
      fitSummary() {
        const fit = this.fit
        if (!fit) return ''
        const parts = [this.$t('{0} short pages before', [fit.shortBefore])]
        if (fit.after) parts.push(this.$t('{0} after', [fit.after.shortPages]))
        if (fit.requestedBy) parts.push(`${this.$t('requested by')} ${fit.requestedBy}`)
        return parts.join(' · ')
      },
      /** A generated text, finished or approved, not running (rest-api answers 409 otherwise). */
      canFit() {
        const generated = !!(this.book.content && this.book.content.source === 'gravur')
        const finished = this.status === 'completed' || !!this.generation.approvedAt
        return generated && finished && !this.running
      },
      anyAction() {
        return (
          this.cancelling ||
          this.fitting ||
          this.generating ||
          this.approving ||
          this.regeneratingBook ||
          this.switchingType ||
          Object.values(this.regenerating).some(Boolean)
        )
      },
      groups() {
        return groupWarnings(this.generation.warnings)
      },
      meta() {
        const g = this.book.generation
        if (!g) return []
        const parts = []
        if (g.checkedAt) parts.push(`${this.$t('checked')} ${this.formatTime(g.checkedAt)}`)
        if (g.requests) parts.push(`${g.requests} ${this.$tc('request', g.requests)}`)
        if (g.locale) parts.push(g.locale.toUpperCase())
        if (g.jobId) parts.push(`${this.$t('Job')} ${g.jobId.slice(0, 8)}`)
        if (g.approvedBy) {
          parts.push(`${this.$t('approved by')} ${g.approvedBy}${g.approvedAt ? `, ${this.formatTime(g.approvedAt)}` : ''}`)
        }
        if (g.status === 'cancelled' && g.cancelledAt) {
          const by = g.cancelledBy || 'gravur'
          parts.push(`${this.$t('cancelled by')} ${by}, ${this.formatTime(g.cancelledAt)}`)
        }
        return parts
      },
    },
    watch: {
      status() {
        this.confirmApprove = false
        this.confirmRegenerate = false
        this.confirmCancel = false
      },
      'book.bookType'(type) {
        this.selectedType = type || null
      },
    },
    methods: {
      genderName,
      warningLabel,
      formatTime(value) {
        return moment(value).format('DD.MM.YYYY HH:mm')
      },
      async generate() {
        this.generating = true
        try {
          const { data } = await generateHoroscopeContent(this.orderId, this.book.lineItemId, this.storeId)
          this.$emit('view', data)
          this.$emit(
            'notify',
            this.$t(data.status === 'exists' ? 'A text job already exists for this book' : 'Text requested'),
            'success'
          )
        } catch (error) {
          this.fail(error, 'Could not request the text')
        } finally {
          this.generating = false
        }
      },
      /** Saves the type as the book's override (the same switch as in the book data), then Generate writes that type. */
      async switchType(type) {
        if (!type || type === this.book.bookType) return
        const previous = this.book.bookType
        this.selectedType = type
        this.switchingType = true
        try {
          const { data } = await updateHoroscopeBook(this.orderId, this.book.lineItemId, this.storeId, { bookType: type })
          this.$emit('view', data)
          this.$emit('notify', this.$t('Book type saved'), 'success')
        } catch (error) {
          this.selectedType = previous || null
          this.fail(error, 'Could not change the book type')
        } finally {
          this.switchingType = false
        }
      },
      async regenerateBook() {
        this.regeneratingBook = true
        try {
          const { data } = await generateHoroscopeContent(this.orderId, this.book.lineItemId, this.storeId, {
            regenerate: true,
          })
          this.confirmRegenerate = false
          this.$emit('view', data)
          this.$emit(
            'notify',
            this.$t(data.status === 'exists' ? 'A text job already exists for this book' : 'The whole book is being written again'),
            'success'
          )
        } catch (error) {
          this.fail(error, 'Could not regenerate the book')
        } finally {
          this.regeneratingBook = false
        }
      },
      /** `finished`: gravur had already finished the job — the panel then shows what it finished as. */
      async cancelGeneration() {
        this.cancelling = true
        try {
          const { data } = await cancelHoroscopeGeneration(this.orderId, this.book.lineItemId, this.storeId)
          this.confirmCancel = false
          this.$emit('view', data)
          if (data.status === 'finished') {
            this.$emit('notify', this.$t('Too late to cancel — gravur had already finished the text'), 'warning')
          } else {
            this.$emit('notify', this.$t('Generation cancelled'), 'success')
          }
        } catch (error) {
          this.fail(error, 'Could not cancel the generation')
        } finally {
          this.cancelling = false
        }
      },
      async fitPages() {
        this.fitting = true
        try {
          const { data } = await fitHoroscopePages(this.orderId, this.book.lineItemId, this.storeId)
          this.$emit('view', data)
          const fit = data.generation && data.generation.fit
          this.$emit(
            'notify',
            this.$t(fit && fit.status === 'requested' ? 'Chapters are being rewritten to fit their pages' : 'Nothing to fit'),
            'success'
          )
        } catch (error) {
          this.fail(error, 'Could not fit the pages')
        } finally {
          this.fitting = false
        }
      },
      async approve() {
        this.approving = true
        try {
          const { data } = await approveHoroscopeContent(this.orderId, this.book.lineItemId, this.storeId)
          this.confirmApprove = false
          this.$emit('view', data)
          this.$emit('notify', this.$t('Text approved — the book is assembled next'), 'success')
        } catch (error) {
          this.fail(error, 'Could not approve the text')
        } finally {
          this.approving = false
        }
      },
      async regenerate(sectionId) {
        this.$set(this.regenerating, sectionId, true)
        try {
          const { data } = await regenerateHoroscopeSection(
            this.orderId,
            this.book.lineItemId,
            sectionId,
            this.storeId
          )
          this.$emit('view', data)
          this.$emit('notify', this.$t('Section is being rewritten'), 'success')
        } catch (error) {
          this.fail(error, 'Could not regenerate the section')
        } finally {
          this.$set(this.regenerating, sectionId, false)
        }
      },
      /**
       * 409: the book or job is not in a state for this (e.g. still running) — a
       * warning, then refresh so the panel shows why. 422 carries the view
       * (unsupported / invalid book). 404 from gravur: section or job unknown.
       */
      fail(error, fallback) {
        const response = error.response || {}
        const body = response.data || {}
        const upstream = typeof body === 'object' ? body.message : null
        if (body && typeof body === 'object' && body.lineItemId) this.$emit('view', body)

        let message = upstream || this.$t(fallback)
        if (response.status === 409) message = `${this.$t('Not possible right now')}: ${message}`
        else if (response.status === 404) {
          message = `${this.$t('gravur does not know this section or job')}: ${message.replace(/^gravur: /, '')}`
        }
        else if (response.status === 503) message = this.$t('Text generation is not configured on the server')

        this.$emit('notify', message, [404, 409, 422].includes(response.status) ? 'warning' : 'error')
        if (response.status === 409 || response.status === 404) this.$emit('refresh')
      },
    },
  }
</script>

<style scoped>
  .generation-panel {
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.02);
  }
  .warnings-table {
    background: transparent !important;
  }
  .section-cell {
    width: 34%;
    vertical-align: top;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }
  .type-select {
    max-width: 210px;
  }
  .action-cell {
    width: 1%;
    white-space: nowrap;
  }
</style>
